/* Grondyke · PDP upsell popup controller (window.GkUpsell).

   Shown in place of the cart drawer for one add, when the main buy form on a PDP that has the
   popup enabled completes successfully. The drawer is not suppressed, it is deferred: whichever
   way the shopper answers, the drawer opens afterwards, so the flow still ends where it always
   ended.

   ARMING is one-shot and explicit. gk-main-product.js calls arm() immediately before the main
   form's GkCart.add, and consumeArmed() clears the flag as it reads it. The quick-add bands on
   the same page go through the same GkCart.add but never arm, so they keep the drawer.

   SWAP removes only what this add put in the cart. /cart/add.js returns the line's TOTAL quantity
   after the add (verified against the live cart, not assumed), so the prior quantity is
   total - requested. A shopper who already had two of something and added one more is put back
   to two rather than losing all three.

   TRACKING reports five outcomes: shown, accept, swap, dismiss and suppressed. Suppressed is
   reported too, because the popup is one-shot per session and is skipped when the cart already
   holds the offer, and counting only the times it rendered would flatter the accept rate.
   Each one goes to the GTM dataLayer, to Shopify's customer events, and out as a DOM event.
   Accepting also writes a line item property and a cart attribute, so the order itself carries
   the evidence and the revenue question can be answered without depending on browser analytics.

   Everything here fails open: any missing setting, missing element or rejected request falls back
   to opening the drawer, because a shopper who has just paid attention to an add-to-cart must
   never be left staring at nothing. Tracking is wrapped the same way: an analytics failure must
   never cost the shopper the add. */
(function () {
  'use strict';

  var root = null;
  var pendingArm = false;
  var ctx = null; /* { key, priorQty } for the line this add created */
  var lastFocus = null;

  function el(sel) { return root ? root.querySelector(sel) : null; }

  function attr(name) { return (root && root.getAttribute(name)) || ''; }

  function money(cents) { return Math.round(Number(cents) || 0) / 100; }

  /* One payload shape for every event, so a single set of GA4 custom definitions covers them. */
  function payload(outcome, extra) {
    var price = Number(attr('data-offer-price')) || 0;
    var out = {
      upsell_outcome: outcome,
      upsell_offer_id: attr('data-offer-id'),
      upsell_offer_variant_id: String(offerVariantId() || ''),
      upsell_offer_handle: attr('data-offer-handle'),
      upsell_offer_title: attr('data-offer-title'),
      upsell_offer_price: money(price),
      upsell_trigger_id: attr('data-trigger-id'),
      upsell_trigger_handle: attr('data-trigger-handle'),
      upsell_trigger_title: attr('data-trigger-title'),
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
      value: money(price)
    };
    if (extra) { for (var k in extra) { if (Object.prototype.hasOwnProperty.call(extra, k)) out[k] = extra[k]; } }
    return out;
  }

  /* dataLayer for GTM and GA4, Shopify.analytics for any custom web pixel, and a DOM event for
     anything in the theme that wants to listen. gk:upsell:shown keeps the name it already had. */
  function track(outcome, extra) {
    var data;
    try { data = payload(outcome, extra); } catch (e) { return; }
    try {
      window.dataLayer = window.dataLayer || [];
      var row = { event: 'gk_upsell_' + outcome };
      for (var k in data) { if (Object.prototype.hasOwnProperty.call(data, k)) row[k] = data[k]; }
      window.dataLayer.push(row);
    } catch (e) { /* analytics is never allowed to break the dialog */ }
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish('gk_upsell_' + outcome, data);
      }
    } catch (e) { /* no-op */ }
    try {
      document.dispatchEvent(new CustomEvent('gk:upsell:' + outcome, { detail: data }));
    } catch (e) { /* no-op */ }
  }

  function alpine() {
    var A = window.Alpine;
    if (!A || !A.store) return null;
    return A;
  }

  function openDrawer() {
    var A = alpine();
    try {
      if (A && A.store('xMiniCart')) A.store('xMiniCart').openCart();
    } catch (e) { /* the drawer is a nicety here, never a hard dependency */ }
  }

  function seenKey() {
    return (root && root.getAttribute('data-key')) || 'gk-ups';
  }

  function alreadySeen() {
    if (!root || root.getAttribute('data-once') !== '1') return false;
    try {
      return sessionStorage.getItem(seenKey()) === '1';
    } catch (e) {
      return false; /* private mode: show it, rather than suppress it forever */
    }
  }

  function markSeen() {
    if (!root || root.getAttribute('data-once') !== '1') return;
    try { sessionStorage.setItem(seenKey(), '1'); } catch (e) { /* no-op */ }
  }

  function offerVariantId() {
    var v = root && root.getAttribute('data-variant');
    return v ? Number(v) : 0;
  }

  function setError(msg) {
    var e = el('[data-gk-upsell-error]');
    if (!e) return;
    if (msg) { e.textContent = msg; e.hidden = false; } else { e.textContent = ''; e.hidden = true; }
  }

  function busy(on) {
    var btns = root ? root.querySelectorAll('.gk-ups__btn') : [];
    for (var i = 0; i < btns.length; i++) {
      btns[i].disabled = !!on;
      btns[i].classList.toggle('is-loading', !!on);
    }
  }

  function close(thenOpenDrawer) {
    if (!root) return;
    root.hidden = true;
    document.removeEventListener('keydown', onKey, true);
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
    lastFocus = null;
    if (thenOpenDrawer !== false) openDrawer();
  }

  function onKey(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.stopPropagation();
      track('dismiss', { upsell_dismiss_via: 'escape' });
      close(true);
    }
  }

  function post(url, payload) {
    return fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) { return r.json(); }).then(function (res) {
      if (res && res.status && res.status >= 400) {
        throw new Error(res.description || res.message || 'Cart error');
      }
      return res;
    });
  }

  /* Route the offer through GkCart.add rather than posting cart/add.js directly. It requests the
     section renders Eurus needs and applies them, which a bare add does not: the first build of
     this popup added the product correctly and left the drawer painting a stale cart that was
     missing the line it had just created. It also owns the drawer open, so callers must not
     open it a second time. Returns true when GkCart handled the drawer. */
  /* The accepted line carries a property, which is what makes the offer countable in the order
     rather than only in the browser, where an ad blocker is free to drop the event. It is
     underscore prefixed, so the cart drawer and the checkout skip it the way they skip every
     internal property.

     Deliberately NOT a cart attribute: attributes survive /cart/clear.js, so a shopper who took
     the offer, emptied the cart and then bought something else would carry the flag onto an
     order the popup had nothing to do with. The property travels with the line and leaves when
     the line does. */

  function addOffer(mode) {
    var id = offerVariantId();
    if (!id) return Promise.reject(new Error('No offer variant'));
    var buttons = root ? root.querySelectorAll('.gk-ups__btn') : [];
    var item = { id: id, quantity: 1, properties: { _gk_upsell: 'popup-' + mode } };
    if (window.GkCart && window.GkCart.available && window.GkCart.available()) {
      return window.GkCart.add([item], buttons, el('[data-gk-upsell-error]'))
        .then(function (ok) {
          if (!ok) throw new Error('Could not add that. Please try again.');
          return true; /* drawer handled */
        });
    }
    return post('cart/add.js', { items: [item] }).then(function () { return false; });
  }

  /* ── actions ───────────────────────────────────────────────────────────── */

  function doAdd() {
    setError(null);
    busy(true);
    addOffer('accept').then(function (drawerHandled) {
      markSeen();
      track('accept');
      close(!drawerHandled);
    }).catch(function (err) {
      busy(false);
      var msg = err && err.message ? err.message : 'Could not add that. Please try again.';
      setError(msg);
      track('error', { upsell_error: msg, upsell_attempted: 'accept' });
    });
  }

  function doSwap() {
    setError(null);
    if (!ctx || !ctx.key) { doAdd(); return; }
    busy(true);
    /* Put the original line back to the quantity it held before this add, then add the offer.
       Order matters: if the add fails, the shopper is left with neither, so the restore runs
       first only because /cart/change.js is the call far less likely to fail. The catch below
       re-adds nothing, it simply reports, and the cart is still internally consistent. */
    post('cart/change.js', { id: ctx.key, quantity: ctx.priorQty })
      .then(function () { return addOffer('swap'); })
      .then(function (drawerHandled) {
        markSeen();
        /* A swap replaces what the shopper had, so the money this popup actually made is the
           difference, not the price of the offer. Both go out: value carries the difference,
           upsell_offer_price the full figure. */
        var removed = ctx && ctx.removedValue ? ctx.removedValue : 0;
        track('swap', {
          value: money((Number(attr('data-offer-price')) || 0) - removed),
          upsell_swapped_out_value: money(removed),
          upsell_swapped_out_title: (ctx && ctx.title) || ''
        });
        close(!drawerHandled);
      })
      .catch(function (err) {
        busy(false);
        var msg = err && err.message ? err.message : 'Could not swap that. Please try again.';
        setError(msg);
        track('error', { upsell_error: msg, upsell_attempted: 'swap' });
      });
  }

  /* ── public API ────────────────────────────────────────────────────────── */

  window.GkUpsell = {
    arm: function () { pendingArm = true; },

    /* One-shot read. Returns true only if this add should be answered with the popup. */
    consumeArmed: function () {
      var was = pendingArm;
      pendingArm = false;
      if (!was || !root) return false;
      if (!offerVariantId()) return false;
      if (alreadySeen()) { track('suppressed', { upsell_reason: 'seen_this_session' }); return false; }
      return true;
    },

    /* res: the /cart/add.js payload. items: the array that was posted. */
    show: function (res, items) {
      if (!root) { openDrawer(); return; }

      var line = res && res.items && res.items.length ? res.items[0] : null;
      var requested = 0;
      if (items && items.length) {
        /* Only the first line is the main product; an add-on rides along as its own line. */
        requested = Number(items[0].quantity) || 0;
      }
      if (line) {
        var total = Number(line.quantity) || 0;
        /* removedValue is what a swap would take back out, which is the figure the swap event
           reports against: unit price times the quantity this add put in. */
        ctx = {
          key: line.key,
          priorQty: Math.max(0, total - requested),
          /* Held in cents, like data-offer-price, so the swap event converts once. */
          removedValue: (Number(line.final_price) || 0) * requested,
          title: line.product_title || line.title || ''
        };
      } else {
        ctx = null;
      }

      var offerId = offerVariantId();

      /* Never offer something the cart already holds. One read, only on armed adds. */
      fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart.js', {
        headers: { Accept: 'application/json' }
      }).then(function (r) { return r.json(); }).then(function (cart) {
        var held = (cart.items || []).some(function (i) { return i.variant_id === offerId; });
        if (held) { track('suppressed', { upsell_reason: 'already_in_cart' }); openDrawer(); return; }
        render();
      }).catch(function () {
        /* If the cart cannot be read, still show it: a duplicate offer is a smaller failure
           than swallowing the whole interaction. */
        render();
      });
    },

    /* Exposed so a future caller can reuse the dialog without the arming dance. */
    close: close
  };

  function render() {
    lastFocus = document.activeElement;
    setError(null);
    busy(false);
    root.hidden = false;
    document.addEventListener('keydown', onKey, true);
    var first = el('[data-gk-upsell-add]');
    if (first) { try { first.focus({ preventScroll: true }); } catch (e) { first.focus(); } }
    track('shown');
  }

  function bind() {
    root = document.querySelector('[data-gk-upsell]');
    if (!root) return;

    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-gk-upsell-add]')) { doAdd(); return; }
      if (e.target.closest('[data-gk-upsell-swap]')) { doSwap(); return; }
      if (e.target.closest('[data-gk-upsell-dismiss]')) {
        markSeen();
        track('dismiss', { upsell_dismiss_via: e.target.closest('.gk-ups__scrim') ? 'scrim' : 'button' });
        close(true);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
